<template>
    <div>
        <h2>
            Key Information
        </h2>
        <div class="a-table a-table--short-no-style a-table--full-width u-margin-bottom-30">
            <table class="a-table--full-width">
                <tr>
                    <th colspan="2"><h2>The Claimant</h2></th>
                </tr>
                <tr>
                    <td width="66%">Gender</td>
                    <td width="33%">{{gender}}</td>
                </tr>
                <tr>
                    <td>DOB</td>
                    <td>{{claimant.dateOfBirth}}</td>
                </tr>
                <tr>
                    <td>Age At Date Of Injury/Accident ({{claim('dateOfInjury')}})</td>
                    <td>{{ageAtInjury}}</td>
                </tr>
                <tr>
                    <td>Age At Assumed Settlement ({{claim('dateOfAssumedSettlement')}})</td>
                    <td>{{ageAtSettlement}}</td>
                </tr>
                <tr>
                    <td>Age Today ({{today}})</td>
                    <td>{{ageToday}}</td>
                </tr>
            </table>
        </div>
        <div class="a-table a-table--short-no-style a-table--full-width u-margin-bottom-30">
            <table class="a-table--full-width">
                <tr>
                    <th colspan="3"><h2>Income, Absenses From Work &amp; Any Future Periods of Loss</h2></th>
                </tr>
                <tr>
                    <td width="33%" colspan="2">Pre-Accident Net Income per Annum</td>
                    <td width="33%">{{currencyFormat(preAccidentIncome)}}</td>
                </tr>
                <tr>
                    <td colspan="2">Total Payments Made</td>
                    <td>{{currencyFormat(getPostInjuryIncomePeriodPaymentsTotal)}}</td>
                </tr>
                <tbody v-for="period in pastPeriods" v-if="calcLoss(period) > 0">
                <tr>
                    <td rowspan="4">Period Of Reduced Income</td>
                    <td>Start</td>
                    <td>{{period.startOfPeriod}}</td>
                </tr>
                <tr>
                    <td>End</td>
                    <td>{{period.endOfPeriod}}</td>
                </tr>
                <tr>
                    <td>Duration</td>
                    <td>{{period.duration.toFixed(2)}} years</td>
                </tr>
                <tr>
                    <td>Net Income P.A.</td>
                    <td>{{currencyFormat(period.totalNetIncome)}}</td>
                </tr>
                </tbody>
                <tbody v-if="futureLosses">
                <tr>
                    <td rowspan="3">Periods of Future Loss</td>
                    <td>Start</td>
                    <td>{{ futureLossesStart }}</td>
                </tr>
                <tr>
                    <td>End</td>
                    <td>{{ futureLossesEnd }}</td>
                </tr>
                <tr v-if="durationOfPeriod">
                    <td>Duration</td>
                    <td>{{ durationOfPeriod.toFixed(2) }} years</td>
                </tr>
                </tbody>
            </table>
        </div>
        <div class="a-table a-table--short-no-style a-table--full-width u-margin-bottom-30" v-if="futureLosses">
            <table class="a-table--full-width">
                <tr>
                    <th colspan="3"><h2>Multipliers &amp; Discount Factors for Contingencies Other Than Mortality</h2>
                    </th>
                </tr>
                <tbody>
                <tr>
                    <td rowspan="3">Pre-Accident</td>
                    <td>{{ disabledAtAccident ? 'Disabled' : 'Not Disabled' }}</td>
                    <td rowspan="3">
                        Discount Factor: {{ roundValue(preInjuryDiscountFactor, 2) }}
                    </td>
                </tr>
                <tr>
                    <td>{{ employedAtAccident ? 'Employed' : 'Not Employed' }}</td>
                </tr>
                <tr>
                    <td>{{ levelOfEducation }} — Education</td>
                </tr>
                </tbody>
                <tbody>
                <tr>
                    <td rowspan="3">Post-Accident</td>
                    <td>{{ disabledAtAssessment ? 'Disabled' : 'Not Disabled' }}</td>
                    <td rowspan="3">
                        Discount Factor: {{ roundValue(assessmentDiscountFactor, 2) }}
                    </td>
                </tr>
                <tr>
                    <td>{{ employedAtAssessment ? 'Employed' : 'Not Employed' }}</td>
                </tr>
                <tr>
                    <td>{{ levelOfEducation }} — Education</td>
                </tr>
                </tbody>
                <tbody>
                <tr v-if="durationOfPeriod">
                    <td>Duration of Future Loss</td>
                    <td colspan="2">{{ durationOfPeriod.toFixed(2) }} years</td>
                </tr>
                </tbody>
                <tbody>
                <tr>
                    <td>Multiplier</td>
                    <td>Ogden Table 36</td>
                    <td>{{ multiplier.toFixed(2) }}</td>
                </tr>
                </tbody>
            </table>
        </div>
        <h2>
            Heads of Loss
        </h2>
        <control for="percentage" label="Your Percentage of the claim">
            <input autocomplete="off" v-model="percentage"
                   required
                   name="percentage"
                   id="percentage"
                   max="100"
                   min="0"
                   step="0.1"
                   type="number">
        </control>
        <div class="a-table a-table--short-no-style a-table--full-width u-margin-bottom-30">
            <table class="a-table--full-width" cellpadding="0" cellspacing="0">
                <tr>
                    <th width="25%"></th>
                    <th width="25%"><h2>Amount</h2></th>
                    <th width="25%"><h2>Interest</h2></th>
                    <th width="25%"><h2>Apportioned Total</h2></th>
                </tr>
                <tr>
                    <td>General Damages</td>
                    <td>{{ currencyFormat(getGeneralDamages) }}</td>
                    <td v-if="claim('proceduralStatus')==='pre-lit'"></td>
                    <td v-else>
                        {{ currencyFormat(otherPastLoss('generalDamagesWithInterest'))}}
                    </td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('generalDamages')) }}
                    </td>
                </tr>
                <tr v-if="pastLossOfEarnings">
                    <td>Past Loss of Earnings</td>
                    <td>{{ currencyFormat(getIncomeValue('pastLossOfEarningsNetTotal')) }}</td>
                    <td>{{ currencyFormat(otherPastLoss('pastLossOfEarningsWithInterest')) }}</td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('pastLossOfEarnings')) }}
                    </td>
                </tr>
                <tr v-if="otherPastLoss('pastCostOfCare') > 0">
                    <td>Past Cost of Care</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfCare')) }}</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfCareWithInterest')) }}</td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('pastCostOfCare')) }}
                    </td>
                </tr>
                <tr v-if="otherPastLoss('pastCostOfServices') > 0">
                    <td>Past Cost of Services</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfServices')) }}</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfServicesWithInterest')) }}</td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('pastCostOfServices')) }}
                    </td>
                </tr>
                <tr v-if="otherPastLoss('pastCostOfTravel') > 0">
                    <td>Past Cost of Travel</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfTravel')) }}</td>
                    <td>{{ currencyFormat(otherPastLoss('pastCostOfTravelWithInterest')) }}</td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('pastCostOfTravel')) }}
                    </td>
                </tr>
                <tr v-if="otherPastLoss('misc') > 0">
                    <td>Miscellaneous</td>
                    <td>{{ currencyFormat(otherPastLoss('misc')) }}</td>
                    <td>{{ currencyFormat(otherPastLoss('miscWithInterest')) }}</td>
                    <td>
                        {{ currencyFormat(adjustedForPercent('misc')) }}
                    </td>
                </tr>
                <tr v-if="customHeadsOfLossTotal > 0" v-for="headOfLoss in allCustomHeadOfLoss">
                    <td>{{ headOfLoss.name }}</td>
                    <td>{{ currencyFormat(headOfLoss.value) }}</td>
                    <td>{{ currencyFormat(headOfLoss.interestAccrued) }}</td>
                </tr>
                <tr v-if="futurePensionTotal > 0">
                    <td>Future Loss of Pension</td>
                    <td>{{ currencyFormat(futurePensionTotal) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('futurePensionTotal'))}}</td>
                </tr>
                <tr v-if="futureCareTotal > 0">
                    <td>Future Loss of Care</td>
                    <td>{{ currencyFormat(futureCareTotal) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('futureCareTotal'))}}</td>
                </tr>
                <tr v-if="futureServicesTotal > 0">
                    <td>Future Loss of Services</td>
                    <td>{{ currencyFormat(futureServicesTotal) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('futureServicesTotal'))}}</td>
                </tr>
                <tr v-if="purchasesTotal > 0">
                    <td>Future Purchases</td>
                    <td>{{ currencyFormat(purchasesTotal) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('purchasesTotal'))}}</td>
                </tr>
                <tr v-if="lossResult > 0">
                    <td>Future Loss of Earnings</td>
                    <td>{{ currencyFormat(lossResult) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('lossResult'))}}</td>
                </tr>
                <tr v-if="cruTotalDeductions > 0">
                    <td>CRU Benefits</td>
                    <td>- {{ currencyFormat(cruTotalDeductions) }}</td>
                    <td></td>
                    <td>{{currencyFormat(adjustedForPercent('cruTotalDeductions'))}}</td>
                </tr>
                <tfoot>
                <tr class="a-table--grey">
                    <td>Subtotal</td>
                    <td>{{ currencyFormat(subTotal) }}</td>
                    <td>{{ currencyFormat(interestTotal) }}</td>
                    <td>{{currencyFormat(adjustedForPercent('subTotal'))}}</td>
                </tr>
                <tr class="a-table--penultimate" v-if="getAllInterimPaymentsTotal > 0">
                    <td>Total</td>
                    <td colspan="2">{{ currencyFormat(total) }}</td>
                    <td>{{currencyFormat(adjustedForPercent('total'))}}</td>
                </tr>
                <tr>
                    <td>Total <span v-if="getAllInterimPaymentsTotal > 0">less interim payment(s)</span></td>
                    <td colspan="2">{{ currencyFormat(totalLessInterimPayments) }}</td>
                    <td>{{currencyFormat(adjustedForPercent('totalLessInterimPayments'))}}</td>
                </tr>
                </tfoot>
            </table>
        </div>

        <div class="step-button flex-state-buttons flex-state-buttons--block a-table--center">
            <div>
                <router-link :to="{ name: 'personal::step-8' }" class="a-button" tag="button">
                    Back
                </router-link>
                <router-link :to="{ name: 'personal::documents.list', params : { id: claimId } }" class="a-button"
                             tag="button">
                    View Documents
                </router-link>
            </div>
        </div>

        <reserves-for-claim></reserves-for-claim>

    </div>
</template>
<script>
    import * as _ from 'lodash';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import StepButton from 'components/StepButton.vue';
    import {mapActions, mapGetters} from 'vuex';
    import {prevStep} from 'services/routingMethods';
    import currencyFormat from 'mixins/currencyFormat';
    import modal from 'components/modal/Modal.vue';
    import dateFormat from 'mixins/dateFormat';
    import VueHtml5Editor from 'vue-html5-editor';
    import store from 'store/index';
    import roundValue from 'mixins/roundValue';
    import InitialReservesCosts from 'pages/claims-dashboard/components/InitialReservesCosts'
    import ClaimInfo from "pages/claims-dashboard/components/ClaimInfo";
    import moment from "moment";
    import ReservesForClaim from "pages/claims-dashboard/components/ReservesForClaim";

    const HtmlEditor = new VueHtml5Editor();

    const numberVal = value => parseFloat(value || 0);

    export default {
        name: 'calculate-loss',
        data() {
            return {
                editingContent: null,
                valid: false,
                loading: false,
                isSaving: false,
            }
        },
        computed: {
            ...mapGetters('user', [
                'getToken',
            ]),
            ...mapGetters('documents', {
                getGeneratedDocuments: 'getGeneratedDocuments',
                getDocumentsByType: 'getDocumentsByType',
                getEditingDocument: 'getEditingDocument',
                requesting: 'getRequestStatus',
                findDocument: 'getGeneratedDocument'
            }),
            ...mapGetters('personal/interimPayments', ['getAllInterimPaymentsTotal']),
            ...mapGetters('http', ['apiRouteRequestRunning']),
            ...mapGetters('claim', {
                claimId: 'id', theClaim: 'current'
            }),
            ...mapGetters('personal/otherFutureLosses', [
                'careTotal',
                'servicesTotal',
                'purchasesTotal',
                'pensionValue'
            ]),
            ////// Claimant Info Table
            ...mapGetters('personal/claimant', {
                claimant: 'claimant',
                claimantDetail: 'getClaimantDetail',
                claim: 'getClaimInfo',
                getClaimInfo: 'getClaimInfo',
                getClaimantValue: 'getClaimantValue',
                getIncomeValue: 'getIncomeValue',
            }),
            ...mapGetters('personal/futureLosses', [
                'getLoss',
            ]),
            ...mapGetters('personal/cruBenefits', [
                'getGeneralDamagesOffset',
                'getPastLossOfEarningsOffset',
                'getPastCostOfCareOffset',
                'getPastCostOfTravelOffset'
            ]),

            futureLosses: {
                get() {
                    return this.getLoss('futureLosses');
                },
            },
            futureLossesStart: {
                get() {
                    return this.getLoss('futureLossesStart');
                },
            },
            futureLossesEnd: {
                get() {
                    return this.getLoss('futureLossesEnd');
                },
            },
            durationOfPeriod: {
                get() {
                    if (!this.futureLossesEnd || !this.futureLossesStart) {
                        return false;
                    }

                    return moment(this.futureLossesEnd, 'DD/MM/YYYY')
                        .diff(moment(this.futureLossesStart, 'DD/MM/YYYY'), 'years', true);
                }
            },
            multiplier: {
                get() {
                    return this.getLoss('multiplier');
                }
            },
            preInjuryDiscountFactor: {
                get() {
                    return this.getLoss('preInjuryDiscountFactor');
                }
            },
            assessmentDiscountFactor: {
                get() {
                    return this.getLoss('assessmentDiscountFactor');
                }
            },
            lossResult: {
                get() {
                    return this.getIncomeValue('preAccidentFutureLossOfEarningsNetTotal') - this.getIncomeValue('futureLossOfEarningsNetTotal');
                }
            },
            levelOfEducation: {
                get() {
                    return this.getClaimantValue('levelOfEducation');
                },
                set(value) {
                    this.updateClaimantValue({key: 'levelOfEducation', value});
                }
            },
            disabledAtAccident: {
                get() {
                    return this.getClaimantValue('disabledAtAccident');
                },
                set(value) {
                    this.updateClaimantValue({key: 'disabledAtAccident', value});
                }
            },
            employedAtAccident: {
                get() {
                    return this.getClaimantValue('employedAtAccident');
                },
                set(value) {
                    this.updateClaimantValue({key: 'employedAtAccident', value});
                }
            },
            disabledAtAssessment: {
                get() {
                    return this.getClaimantValue('disabledAtTrial');
                },
                set(value) {
                    this.updateClaimantValue({key: 'disabledAtTrial', value});
                }
            },
            employedAtAssessment: {
                get() {
                    return this.getClaimantValue('employedAtTrial');
                },
                set(value) {
                    this.updateClaimantValue({key: 'employedAtTrial', value});
                }
            },
            futureCareTotal: {
                get() {
                    return this.careTotal;
                },
            },
            futureServicesTotal: {
                get() {
                    return this.servicesTotal;
                },
            },
            futurePensionTotal: {
                get() {
                    return this.pensionValue('totalLoss');
                },
            },
            gender() {
                return _.startCase(this.claimant.gender);
            },
            ageAtInjury() {
                return moment(this.claim('dateOfInjury'), 'DD/MM/YYYY')
                    .diff(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY'), 'years');
            },
            ageAtSettlement() {
                return moment(this.claim('dateOfAssumedSettlement'), 'DD/MM/YYYY')
                    .diff(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY'), 'years');
            },
            ageToday() {
                return moment()
                    .diff(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY'), 'years');
            },
            today() {
                return moment().format("DD/MM/YYYY");
            },

            ////// INCOME, ABSENCES FROM WORK & ANY FUTURE PERIODS OF LOSS
            ...mapGetters('personal/claimant', {
                getIncomeValue: 'getIncomeValue',
                pastPeriodsSubTotal: 'pastPeriodsSubTotal',
                pastAbsenceTotal: 'pastAbsenceTotal',
                pastIncomeTotal: 'pastIncomeTotal',
                getPostInjuryIncomePeriodPaymentsTotal: 'getPostInjuryIncomePeriodPaymentsTotal'
            }),
            ...mapGetters('personal/calculateLoss',['percent']),
            preAccidentIncome() {
                return this.getIncomeValue('preAccident').totalNetIncome;
            },
            totalPaymentsMade() {
                return this.getIncomeValue('otherPayments');
            },
            pastPeriods() {
                return this.getIncomeValue('pastPeriods');
            },
            pastLossOfEarnings() {
                return this.getIncomeValue('pastLossOfEarnings');
            },

            //// Heads of Loss
            ...mapGetters('personal/generalDamages', {getGeneralDamages: 'getSubTotalDiscounted'}),
            ...mapGetters('personal/otherPastLosses', {
                otherPastLoss: 'value',
                allCustomHeadOfLoss: 'getAllCustomHeadOfLoss',
                customPastLoss: 'getCustomHeadOfLoss',
                customHeadsOfLossTotal: 'customHeadsOfLossTotal',
                customHeadsOfLossTotalWithInterest: 'customHeadsOfLossTotalWithInterest'
            }),
            interestTotal() {
                return numberVal(this.otherPastLoss('generalDamagesWithInterest')) +
                    numberVal(this.otherPastLoss('pastLossOfEarningsWithInterest')) +
                    numberVal(this.otherPastLoss('pastCostOfCareWithInterest')) +
                    numberVal(this.otherPastLoss('pastCostOfServicesWithInterest')) +
                    numberVal(this.otherPastLoss('pastCostOfTravelWithInterest')) +
                    numberVal(this.otherPastLoss('miscWithInterest')) +
                    numberVal(this.customHeadsOfLossTotalWithInterest);
            },
            subTotal() {
                return numberVal(this.getGeneralDamages) +
                    numberVal(this.getIncomeValue('pastLossOfEarningsNetTotal')) +
                    numberVal(this.otherPastLoss('pastCostOfCare')) +
                    numberVal(this.otherPastLoss('pastCostOfServices')) +
                    numberVal(this.otherPastLoss('pastCostOfTravel')) +
                    numberVal(this.otherPastLoss('misc')) +
                    numberVal(this.customHeadsOfLossTotal) +
                    numberVal(this.lossResult) +
                    numberVal(this.futurePensionTotal) +
                    numberVal(this.futureCareTotal) +
                    numberVal(this.purchasesTotal) +
                    numberVal(this.futureServicesTotal) -
                    numberVal(this.cruTotalDeductions);
            },
            total() {
                return this.subTotal + this.interestTotal;
            },
            totalLessInterimPayments() {
                return this.total - numberVal(this.getAllInterimPaymentsTotal);
            },
            downloadUrl() {
                return '/api/v1/download/' + this.claimId + '?access_token=' + this.getToken;
            },
            cruTotalDeductions() {
                return this.getGeneralDamagesOffset +
                    this.getPastLossOfEarningsOffset +
                    this.getPastCostOfCareOffset +
                    this.getPastCostOfTravelOffset;
            },
            uniqueId() {
                return this._uid;
            },
            percentage :{
                get() {
                    return this.percent * 100;
                },
                set(value) {
                    this.setPercent(value / 100);
                }
            }
        },
        mixins: [
            currencyFormat,
            dateFormat,
            roundValue,
        ],
        components: {
            ReservesForClaim,
            modal,
            Control,
            ControlError,
            HtmlEditor,
            StepButton
        },
        methods: {
            back() {
                if (this.prevValid) {
                    this.$router.push(prevStep().route);
                }
            },
            ...mapActions('modal', ['toggle']),
            ...mapActions('documents', {
                toggleContent: 'toggleEditingDocument',
                createGeneratedDocument: 'createGeneratedDocument',
                deleteGeneratedDocument: 'deleteGeneratedDocument',
                getGeneratedDocumentsFromServer: 'getGeneratedDocumentsFromServer',
            }),
            ...mapActions('claim', ['updateClaim', 'setCurrent']),
            ...mapActions('personal/calculateLoss', ['setPercent']),
            createDocument(type) {
                this.createGeneratedDocument({claimId: this.claimId, claimType: store.getters['claim/type'], type});
            },
            deleteDocument(document) {
                if (this.requesting) {
                    return;
                }
                this.deleteGeneratedDocument(document);
            },
            theDocument(index) {
                return this.findDocument(index);
            },
            requestInProgress() {
                return this.apiRouteRequestRunning('/api/v1/generated-documents/load')
            },
            calcLoss(period) {
                return (this.preAccidentIncome - (period.totalNetIncome || 0)) * period.duration;
            },
            adjustedForPercent(key) {
                let value = 0;
                switch(key) {
                    case "generalDamages":
                        value = this.getGeneralDamages;
                        if (this.claim('proceduralStatus')==='pre-lit') {
                            value += this.otherPastLoss('generalDamagesWithInterest');
                        }
                        break;
                    case "pastLossOfEarnings":
                        value = this.getIncomeValue('pastLossOfEarningsNetTotal') + (this.otherPastLoss('pastLossOfEarningsWithInterest') || 0);
                        break;
                    case 'pastLossOfEarnings':
                    case 'pastCostOfServices':
                    case 'pastCostOfTravel':
                    case 'misc':
                        value = this.otherPastLoss(key) + (this.otherPastLoss(key+'WithInterest'));
                        break;
                    case 'subTotal':
                        value = this.subTotal + this.interestTotal;
                    default:
                        value = this[key];

                }
                return value * this.percent;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// CalculateLoss.vue?5c7ec8b2